import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { IDropdownMenuComponent, IIconComponent, IInputDirective } from "@front/m19-ui";
import { KeyValuePipe, NgClass } from "@angular/common";
import { TranslocoDirective } from "@jsverse/transloco";
import { Metric } from "@front/m19-metrics";
import { CurrencyStat } from "@front/m19-models";
import { ITooltipDirective } from "@front/m19-ui";
import { groupByCategory } from "@m19-board/models/MetricsDef";
import { MAX_METRICS } from "@m19-board/insights/metric-selector/metric-selector.component";
import { FormsModule } from "@angular/forms";
import { MetricDescriptionPipe } from "@front/m19-utils";

@Component({
  selector: "app-metrics-menu",
  imports: [
    KeyValuePipe,
    TranslocoDirective,
    ITooltipDirective,
    NgClass,
    IInputDirective,
    FormsModule,
    IIconComponent,
    MetricDescriptionPipe,
    IDropdownMenuComponent,
  ],
  template: ` <div class="relative flex flex-col !overflow-hidden !p-0" iDropdownMenu *transloco="let t">
    <div class="sticky top-0 z-20 flex items-center border-b bg-white p-2" (click)="$event.stopPropagation()">
      <input iInput [(ngModel)]="filter" (click)="$event.stopPropagation()" autoFocus size="sm" />
    </div>

    <ul class="scrollbar relative flex flex-col">
      @for (c of filteredGroupedMetrics() | keyvalue; track c) {
        <li
          class="dd-item sticky top-0 z-10 cursor-default border-b !border-none bg-white font-semibold hover:bg-white"
          (click)="$event.stopPropagation()"
        >
          {{ t(c.key) }}
        </li>
        @for (m of c.value; track m) {
          @let unselectable =
            metricsInSelector().length >= MAX_METRICS && !isMetricInSelector(m) && isNewMetricSelector();
          <li
            class="dd-item"
            [iTooltip]="
              unselectable ? t('metric-selector.max_metrics_reached', { max: MAX_METRICS }) : (m | metricDescription)
            "
            iTooltipPlacement="left"
            (click)="unselectable ? $event.stopPropagation() : clickedOnMetric(m)"
            [ngClass]="{ 'cursor-not-allowed opacity-50': unselectable }"
          >
            <div class="flex w-full items-center justify-between align-middle">
              {{ t("metrics." + m.id + "_title") }}
              @if (isMetricInSelector(m)) {
                <IIcon name="check" class="mr-3" />
              }
            </div>
          </li>
        }
      }
    </ul>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsMenuComponent<T extends CurrencyStat> {
  readonly MAX_METRICS = MAX_METRICS;

  availableMetrics = input.required<Metric<T>[]>();
  metricsInSelector = input.required<Metric<T>[]>();
  isNewMetricSelector = input<boolean>(false);

  toggleMetric = output<Metric<T>>();

  filteredGroupedMetrics = computed(() => {
    const metricsMatchingFilter = this.availableMetrics().filter((m) => this.showMetric(m, this.filter()));
    return groupByCategory(metricsMatchingFilter);
  });

  filter = signal("");

  clickedOnMetric(metric: Metric<T>): void {
    this.toggleMetric.emit(metric);
  }

  isMetricInSelector(m: Metric<T>): boolean {
    return this.metricsInSelector().includes(m);
  }

  private showMetric(metric: Metric<T>, filter: string) {
    return metric.title.toLowerCase().includes(filter.toLowerCase());
  }
}
